import {
  Alarm,
  AlarmAlertGroup,
  useGetAlertGroupQuery,
} from '../../gql/generated';
import { formatMetricName } from '../../utils/formatMetric';
import { uniqueMachineNames } from '../../utils/machineDisplay';
import { formatDateTime } from '../../utils/notificationFormatters';
import { InfoRow, InfoSectionHeader } from '../SidePanel';

interface AlarmGroupDetailsTabProps {
  group: AlarmAlertGroup;
  /** The member alerts of the group, one per signal. */
  alarms: Alarm[];
}

/** A group switches as a whole, so a mixed state reads as its own answer. */
function enabledState(alarms: Alarm[]): string {
  if (alarms.every((alarm) => alarm.isEnabled)) return 'Enabled';
  if (alarms.every((alarm) => !alarm.isEnabled)) return 'Disabled';
  return 'Partially enabled';
}

export function AlarmGroupDetailsTab({
  group,
  alarms,
}: AlarmGroupDetailsTabProps) {
  const { data } = useGetAlertGroupQuery({ variables: { id: group.id } });
  const alertGroup = data?.alertGroup;

  const signalNames = alarms
    .map((alarm) => formatMetricName(alarm.signal))
    .join(', ');

  const machineNames = uniqueMachineNames(alarms).join(', ');

  return (
    <div className='flex flex-col'>
      <section>
        <InfoSectionHeader>Pulse alert</InfoSectionHeader>
        <div className='flex flex-col gap-2 px-6 py-4'>
          <InfoRow label='Name' value={group.name} />
          <InfoRow label='Status' value={enabledState(alarms)} />
          <InfoRow label='Triggered' value={group.isTriggered ? 'Yes' : 'No'} />
          <InfoRow
            label='Created'
            value={formatDateTime(alertGroup?.createdAt)}
          />
          <InfoRow label='Owner' value={alertGroup?.userId} />
        </div>
      </section>

      <section>
        <InfoSectionHeader>Assignments</InfoSectionHeader>
        <div className='flex flex-col gap-2 px-6 py-4'>
          <InfoRow
            label='Signals'
            value={signalNames || 'No signals assigned'}
          />
          <InfoRow
            label='Machines'
            value={machineNames || 'No machines assigned'}
          />
        </div>
      </section>
    </div>
  );
}
